/**
 * 音の特徴量 → サムネイルアニメーションの駆動信号。
 *
 * peaks には周波数情報が無いため、low / mid / high は物理的な帯域ではなく
 * 「エンベロープを 3 つの時定数でローパスした差分」= 変化の速さで擬似的に
 * 分解したものを指す。視覚的には帯域が動いている印象になるが実際の周波数では
 * ない点に注意すること。
 *
 * 再生中のトラックに限れば AnalyserNode で本物の帯域が取れるので、そのときは
 * createSpectrumDriver を使う。どちらも DriveSignal を返すので、レンダラーは
 * 出どころを意識しない。
 */

import { AudioCategory } from '../../../audio-preview/core/audioCategory';
import { PeaksMotion, sampleEnvelope } from '../../../audio-preview/core/peaksMotion';
import { SpectrumFrame } from '../../../audio-preview/core/spectrumFeatures';

export interface DriveSignal {
  /** 現在の音量（0-1） */
  level: number;
  /** 遅い変化成分（0-1） */
  low: number;
  /** 中速の変化成分（0-1） */
  mid: number;
  /** 速い変化成分（0-1） */
  high: number;
  /** オンセットで立ち上がり減衰するパルス（0-1） */
  beat: number;
}

/** カテゴリごとの動きの性格 */
export interface MotionPreset {
  /** 音量へのブロブ半径の追従量 */
  levelGain: number;
  /** 擬似帯域へのブロブ半径の追従量 */
  bandGain: number;
  /** オンセットパルスによる全体の膨張量 */
  beatGain: number;
  /** 無音時も続く自律ドリフトの速度倍率 */
  driftSpeed: number;
  /** 音に反応してブロブが動く距離の倍率 */
  orbitGain: number;
  /** 音量による雲全体の旋回の加速量 */
  swirlGain: number;
  /** オンセットでブロブを中心から外へ弾く量 */
  beatKick: number;
}

/**
 * MUSIC はビート、SoundEffects は擬似帯域、VOICE は音量に反応させる。
 * neutral は音に反応させず、ゆっくりした自律ドリフトのみ。
 */
export const MOTION_PRESETS: Record<AudioCategory, MotionPreset> = {
  music: {
    levelGain: 0.24,
    bandGain: 0.3,
    beatGain: 0.5,
    driftSpeed: 1.15,
    orbitGain: 1.2,
    swirlGain: 2.2,
    beatKick: 0.5,
  },
  sound_effect: {
    levelGain: 0.2,
    bandGain: 0.75,
    beatGain: 0.34,
    driftSpeed: 1.5,
    orbitGain: 1.7,
    swirlGain: 1.4,
    beatKick: 0.7,
  },
  voice: {
    levelGain: 0.6,
    bandGain: 0.24,
    beatGain: 0.18,
    driftSpeed: 0.8,
    orbitGain: 0.8,
    swirlGain: 1,
    beatKick: 0.2,
  },
  // カテゴリ不明。性格を演出すると誤った印象を与えるので、どの音でも確実に
  // 取れる音量だけに反応させる。ただし無反応にはしない（属性なしのファイルが
  // 一番多く、そこが動かないと「壊れている」ように見える）
  neutral: {
    levelGain: 0.42,
    bandGain: 0.12,
    beatGain: 0.14,
    driftSpeed: 0.85,
    orbitGain: 0.7,
    swirlGain: 0.9,
    beatKick: 0.18,
  },
};

/** ローパスの時定数（秒） */
const TAU_SLOW = 0.45;
const TAU_MID = 0.11;
const TAU_FAST = 0.025;
/** 再生停止時に信号が 0 へ戻る時定数（秒） */
const TAU_RELEASE = 0.35;
/** 擬似帯域の差分をこの倍率で 0-1 に持ち上げる */
const MID_BAND_GAIN = 3.2;
const HIGH_BAND_GAIN = 4.5;
/** この強さを超えるオンセットでビートパルスを打つ */
const ONSET_THRESHOLD = 0.05;
/** スペクトル駆動時のオンセットしきい値（flux は 0-1 に正規化済み） */
const SPECTRUM_ONSET_THRESHOLD = 0.22;
/** スペクトル駆動のビートパルスの減衰時定数（秒） */
const SPECTRUM_BEAT_TAU = 0.18;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** dt と時定数から一次ローパスの係数を求める */
const lowpassAlpha = (dt: number, tau: number): number => 1 - Math.exp(-dt / tau);

export interface AudioDriver {
  /**
   * 1 フレーム分進める。
   * @param timeSec 再生位置（秒）
   * @param dtSec 前フレームからの経過秒
   * @param active 再生中か（false なら信号を減衰させる）
   */
  update(_timeSec: number, _dtSec: number, _active: boolean): DriveSignal;
}

/**
 * peaks 由来のエンベロープから駆動信号を生成するステートフルなドライバを作る。
 * 波形が未取得（null）の場合は常に 0 を返し、レンダラーは自律ドリフトのみになる。
 */
export function createAudioDriver(motion: PeaksMotion | null): AudioDriver {
  let slow = 0;
  let mid = 0;
  let fast = 0;
  let beat = 0;
  let previousLevel = 0;

  // BPM が判っていればパルスの減衰をテンポに合わせる（速い曲ほど短く切れる）
  const beatTau = motion?.bpm
    ? Math.min(0.5, Math.max(0.12, 30 / motion.bpm))
    : 0.2;

  const idle: DriveSignal = { level: 0, low: 0, mid: 0, high: 0, beat: 0 };

  return {
    update(timeSec: number, dtSec: number, active: boolean): DriveSignal {
      const dt = Math.min(0.1, Math.max(0.001, dtSec));

      if (!motion || motion.envelope.length === 0) return idle;

      const target = active ? sampleEnvelope(motion.envelope, timeSec) : 0;
      const releaseAlpha = lowpassAlpha(dt, TAU_RELEASE);

      slow += (target - slow) * (active ? lowpassAlpha(dt, TAU_SLOW) : releaseAlpha);
      mid += (target - mid) * (active ? lowpassAlpha(dt, TAU_MID) : releaseAlpha);
      fast += (target - fast) * (active ? lowpassAlpha(dt, TAU_FAST) : releaseAlpha);

      const onset = active ? target - previousLevel : 0;
      previousLevel = target;

      beat *= Math.exp(-dt / beatTau);
      if (onset > ONSET_THRESHOLD) {
        beat = Math.max(beat, clamp01(onset * 4));
      }

      return {
        level: clamp01(fast),
        low: clamp01(slow),
        mid: clamp01(Math.abs(mid - slow) * MID_BAND_GAIN),
        high: clamp01(Math.abs(fast - mid) * HIGH_BAND_GAIN),
        beat: clamp01(beat),
      };
    },
  };
}

/**
 * AnalyserNode の実スペクトルから駆動信号を作る。
 *
 * low / mid / high が擬似ではなく本物の帯域になるので、とくに SoundEffects の
 * 「周波数帯域に応じた動き」が意図どおりになる。AnalyserNode 側で既に
 * smoothingTimeConstant が効いているため、ここでの平滑化は軽めでよい。
 *
 * @param read 現フレームの特徴量を返す関数。解析が外れていれば null を返す
 */
export function createSpectrumDriver(read: () => SpectrumFrame | null): AudioDriver {
  let level = 0;
  let low = 0;
  let mid = 0;
  let high = 0;
  let beat = 0;

  return {
    update(_timeSec: number, dtSec: number, active: boolean): DriveSignal {
      const dt = Math.min(0.1, Math.max(0.001, dtSec));
      const frame = active ? read() : null;

      // 解析が取れないフレームは 0 へ向けて減衰させる（急に固まらないように）
      const attack = lowpassAlpha(dt, TAU_MID);
      const release = lowpassAlpha(dt, TAU_RELEASE);
      const alpha = frame ? attack : release;

      level += ((frame?.level ?? 0) - level) * alpha;
      low += ((frame?.low ?? 0) - low) * (frame ? lowpassAlpha(dt, TAU_SLOW) : release);
      mid += ((frame?.mid ?? 0) - mid) * alpha;
      high += ((frame?.high ?? 0) - high) * (frame ? lowpassAlpha(dt, TAU_FAST) : release);

      beat *= Math.exp(-dt / SPECTRUM_BEAT_TAU);
      if (frame && frame.flux > SPECTRUM_ONSET_THRESHOLD) {
        beat = Math.max(beat, clamp01(frame.flux));
      }

      return {
        level: clamp01(level),
        low: clamp01(low),
        mid: clamp01(mid),
        high: clamp01(high),
        beat: clamp01(beat),
      };
    },
  };
}
